import React, { useState } from 'react';
import axiosAuth from '../../../config/axios';
import {
  ESTADO_PLAN, PLAN_VIGENTE, ESTADO_ACCION, ESTADO_COMPROMISO, RESPONSABLE_COMPROMISO,
  Chip, fecha, fechaCorta, estilos as s,
} from '../comun';

// Pestaña "Plan" del caso: un plan vigente a la vez, con acciones, indicadores
// y compromisos vinculados. Los planes anteriores quedan como historial.

const ACCION_VACIA = { accion: '', responsable: 'orientacion', fecha: '' };

function progreso(acciones) {
  const cuentan = acciones.filter(a => a.estado !== 'cancelada');
  if (!cuentan.length) return null;
  const hechas = cuentan.filter(a => a.estado === 'cumplida').length;
  return { hechas, total: cuentan.length, pct: Math.round((hechas / cuentan.length) * 100) };
}

// Formulario para crear un plan o editar sus datos
function FormPlan({ plan, casoId, onListo, onCancelar }) {
  const [f, setF] = useState({
    objetivo: plan?.objetivo || '', situacion: plan?.situacion || '', indicadores: plan?.indicadores || '',
    fecha_inicio: fechaCorta(plan?.fecha_inicio) || new Date().toISOString().slice(0, 10),
    fecha_objetivo: fechaCorta(plan?.fecha_objetivo) || '',
  });
  const [acciones, setAcciones] = useState([{ ...ACCION_VACIA }]);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  async function guardar(e) {
    e.preventDefault();
    setOcupado(true); setError('');
    try {
      const datos = { ...f, fecha_inicio: f.fecha_inicio || null, fecha_objetivo: f.fecha_objetivo || null };
      if (plan) {
        await axiosAuth.patch(`/api/bienestar/planes/${plan.id}`, datos);
      } else {
        const lista = acciones.filter(a => a.accion.trim()).map(a => ({ ...a, fecha: a.fecha || null }));
        await axiosAuth.post(`/api/bienestar/casos/${casoId}/planes`, { ...datos, acciones: lista });
      }
      onListo();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el plan');
    } finally {
      setOcupado(false);
    }
  }

  const cambiarAccion = (i, campo, valor) => setAcciones(acciones.map((a, j) => (j === i ? { ...a, [campo]: valor } : a)));

  return (
    <form onSubmit={guardar} style={es.form}>
      <h3 style={es.formTitulo}>{plan ? 'Editar plan' : 'Nuevo plan de acompañamiento'}</h3>
      {error && <div style={s.errorBox}>{error}</div>}
      <div style={s.campo}>
        <label style={s.label}>Objetivo *</label>
        <textarea style={es.area} maxLength={4000} required value={f.objetivo}
          onChange={e => setF({ ...f, objetivo: e.target.value })}
          placeholder="Qué se busca lograr con el estudiante. Ej.: recuperar la asistencia regular a clases." />
      </div>
      <div style={s.campo}>
        <label style={s.label}>Situación identificada</label>
        <textarea style={es.area} maxLength={4000} value={f.situacion}
          onChange={e => setF({ ...f, situacion: e.target.value })}
          placeholder="Describe hechos observados, no conclusiones ni diagnósticos. Ej.: faltó 6 días en septiembre." />
      </div>
      <div style={s.campo}>
        <label style={s.label}>Indicadores de seguimiento</label>
        <textarea style={es.area} maxLength={4000} value={f.indicadores}
          onChange={e => setF({ ...f, indicadores: e.target.value })}
          placeholder="Cómo sabremos que avanza. Ej.: asistencia semanal ≥ 90 %; entrega de tareas." />
      </div>
      <div style={es.fila2}>
        <div style={{ ...s.campo, flex: 1 }}>
          <label style={s.label}>Fecha de inicio</label>
          <input type="date" style={s.input} value={f.fecha_inicio} onChange={e => setF({ ...f, fecha_inicio: e.target.value })} />
        </div>
        <div style={{ ...s.campo, flex: 1 }}>
          <label style={s.label}>Fecha objetivo</label>
          <input type="date" style={s.input} min={f.fecha_inicio || undefined} value={f.fecha_objetivo} onChange={e => setF({ ...f, fecha_objetivo: e.target.value })} />
        </div>
      </div>

      {!plan && (
        <div style={s.campo}>
          <label style={s.label}>Acciones</label>
          {acciones.map((a, i) => (
            <div key={i} style={es.accionNueva}>
              <input style={{ ...s.input, flex: '2 1 220px' }} maxLength={500} placeholder="Acción" value={a.accion} onChange={e => cambiarAccion(i, 'accion', e.target.value)} />
              <select style={{ ...s.input, flex: '1 1 130px' }} value={a.responsable} onChange={e => cambiarAccion(i, 'responsable', e.target.value)}>
                {Object.entries(RESPONSABLE_COMPROMISO).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
              </select>
              <input type="date" style={{ ...s.input, flex: '1 1 140px' }} value={a.fecha} onChange={e => cambiarAccion(i, 'fecha', e.target.value)} />
              {acciones.length > 1 && (
                <button type="button" aria-label="Quitar acción" onClick={() => setAcciones(acciones.filter((_, j) => j !== i))} style={es.quitar}>✕</button>
              )}
            </div>
          ))}
          {acciones.length < 30 && (
            <button type="button" onClick={() => setAcciones([...acciones, { ...ACCION_VACIA }])} style={es.enlace}>+ Otra acción</button>
          )}
        </div>
      )}

      <div style={s.botones}>
        <button type="button" onClick={onCancelar} style={s.btnSecundario}>Cancelar</button>
        <button type="submit" disabled={ocupado || !f.objetivo.trim()} style={{ ...s.btnPrimario, opacity: !f.objetivo.trim() ? 0.5 : 1 }}>
          {ocupado ? 'Guardando...' : plan ? 'Guardar cambios' : 'Crear plan'}
        </button>
      </div>
    </form>
  );
}

function Texto({ titulo, valor }) {
  if (!valor) return null;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={es.textoTitulo}>{titulo}</div>
      <div style={es.textoValor}>{valor}</div>
    </div>
  );
}

// Un plan: datos, avance, acciones y compromisos vinculados
function Plan({ plan, caso, editable, hayVigente, onCambio, onEditar, setError }) {
  const [nuevaAccion, setNuevaAccion] = useState({ ...ACCION_VACIA });
  const [nuevoComp, setNuevoComp] = useState({ descripcion: '', responsable_tipo: 'estudiante', fecha_limite: '' });
  const vigente = PLAN_VIGENTE.includes(plan.estado);
  const puedeEditar = editable && vigente;
  const avance = progreso(plan.acciones);
  const compromisos = caso.compromisos.filter(k => k.plan_id === plan.id);
  const hoy = new Date().toISOString().slice(0, 10);

  async function llamar(fn, textoError) {
    setError('');
    try { await fn(); onCambio(); } catch (err) { setError(err.response?.data?.error || textoError); }
  }

  const cambiarEstado = (estado) => {
    if (['cumplido', 'cerrado', 'suspendido'].includes(estado)
      && !window.confirm(`¿Marcar el plan como "${ESTADO_PLAN[estado].nombre.toLowerCase()}"? Ya no se podrán agregar acciones.`)) return;
    llamar(() => axiosAuth.patch(`/api/bienestar/planes/${plan.id}`, { estado }), 'No se pudo cambiar el estado del plan');
  };

  function agregarAccion(e) {
    e.preventDefault();
    if (!nuevaAccion.accion.trim()) return;
    llamar(async () => {
      await axiosAuth.post(`/api/bienestar/planes/${plan.id}/acciones`, { ...nuevaAccion, fecha: nuevaAccion.fecha || null });
      setNuevaAccion({ ...ACCION_VACIA });
    }, 'No se pudo agregar la acción');
  }

  function agregarCompromiso(e) {
    e.preventDefault();
    if (!nuevoComp.descripcion.trim()) return;
    llamar(async () => {
      await axiosAuth.post(`/api/bienestar/casos/${caso.id}/compromisos`, { ...nuevoComp, fecha_limite: nuevoComp.fecha_limite || null, plan_id: plan.id });
      setNuevoComp({ descripcion: '', responsable_tipo: 'estudiante', fecha_limite: '' });
    }, 'No se pudo agregar el compromiso');
  }

  return (
    <div>
      <div style={es.cabecera}>
        <div style={{ minWidth: 0 }}>
          <div style={es.periodo}>
            {plan.fecha_inicio ? `Desde ${fecha(plan.fecha_inicio)}` : `Creado ${fecha(plan.creado_en)}`}
            {plan.fecha_objetivo ? ` · meta ${fecha(plan.fecha_objetivo)}` : ''}
            {plan.creado_por ? ` · ${plan.creado_por}` : ''}
          </div>
        </div>
        <Chip def={ESTADO_PLAN[plan.estado]} />
      </div>

      {avance && (
        <div style={es.avance} aria-label={`Avance: ${avance.hechas} de ${avance.total} acciones cumplidas`}>
          <div style={es.barra}><div style={{ ...es.barraRelleno, width: `${avance.pct}%` }} /></div>
          <span style={es.avanceTexto}>{avance.hechas} de {avance.total} acciones cumplidas</span>
        </div>
      )}

      <Texto titulo="Objetivo" valor={plan.objetivo} />
      <Texto titulo="Situación identificada" valor={plan.situacion} />
      <Texto titulo="Indicadores de seguimiento" valor={plan.indicadores} />

      {editable && (
        <div style={es.acciones}>
          {puedeEditar && <button onClick={onEditar} style={s.btnSecundario}>Editar datos</button>}
          {plan.estado === 'activo' && <button onClick={() => cambiarEstado('en_seguimiento')} style={s.btnSecundario}>Pasar a seguimiento</button>}
          {plan.estado === 'en_seguimiento' && <button onClick={() => cambiarEstado('activo')} style={s.btnSecundario}>Volver a activo</button>}
          {vigente && <button onClick={() => cambiarEstado('cumplido')} style={s.btnSecundario}>Marcar cumplido</button>}
          {vigente && <button onClick={() => cambiarEstado('suspendido')} style={s.btnSecundario}>Suspender</button>}
          {vigente && <button onClick={() => cambiarEstado('cerrado')} style={s.btnSecundario}>Cerrar plan</button>}
          {plan.estado === 'suspendido' && !hayVigente && <button onClick={() => cambiarEstado('activo')} style={s.btnSecundario}>Reactivar</button>}
        </div>
      )}

      <h4 style={es.subtitulo}>Acciones</h4>
      {puedeEditar && (
        <form onSubmit={agregarAccion} style={es.accionNueva}>
          <input style={{ ...s.input, flex: '2 1 220px' }} maxLength={500} placeholder="Nueva acción" value={nuevaAccion.accion} onChange={e => setNuevaAccion({ ...nuevaAccion, accion: e.target.value })} />
          <select style={{ ...s.input, flex: '1 1 130px' }} value={nuevaAccion.responsable} onChange={e => setNuevaAccion({ ...nuevaAccion, responsable: e.target.value })}>
            {Object.entries(RESPONSABLE_COMPROMISO).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
          <input type="date" style={{ ...s.input, flex: '1 1 140px' }} value={nuevaAccion.fecha} onChange={e => setNuevaAccion({ ...nuevaAccion, fecha: e.target.value })} />
          <button type="submit" style={s.btnPrimario}>Agregar</button>
        </form>
      )}
      {plan.acciones.length === 0 ? <div style={es.vacio}>Sin acciones registradas.</div> : (
        <div style={es.lista}>
          {plan.acciones.map(a => {
            const vencida = ['pendiente', 'en_curso'].includes(a.estado) && a.fecha && fechaCorta(a.fecha) < hoy;
            return (
              <div key={a.id} style={{ ...es.item, ...(vencida ? es.vencido : {}) }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ ...es.itemTexto, ...(a.estado === 'cancelada' ? { textDecoration: 'line-through', color: '#999' } : {}) }}>{a.accion}</div>
                  <div style={es.meta}>
                    {RESPONSABLE_COMPROMISO[a.responsable] || a.responsable}
                    {a.fecha ? ` · ${fecha(a.fecha)}` : ''}
                    {vencida && <strong style={{ color: '#c62828' }}> · vencida</strong>}
                  </div>
                </div>
                {puedeEditar ? (
                  <select style={es.selectEstado} aria-label="Estado de la acción" value={a.estado}
                    onChange={e => llamar(() => axiosAuth.patch(`/api/bienestar/plan-acciones/${a.id}`, { estado: e.target.value }), 'No se pudo actualizar la acción')}>
                    {Object.entries(ESTADO_ACCION).map(([v, d]) => <option key={v} value={v}>{d.nombre}</option>)}
                  </select>
                ) : <Chip def={ESTADO_ACCION[a.estado]} />}
              </div>
            );
          })}
        </div>
      )}

      <h4 style={es.subtitulo}>Compromisos vinculados</h4>
      {puedeEditar && (
        <form onSubmit={agregarCompromiso} style={es.accionNueva}>
          <input style={{ ...s.input, flex: '2 1 220px' }} maxLength={500} placeholder="Nuevo compromiso del plan" value={nuevoComp.descripcion} onChange={e => setNuevoComp({ ...nuevoComp, descripcion: e.target.value })} />
          <select style={{ ...s.input, flex: '1 1 130px' }} value={nuevoComp.responsable_tipo} onChange={e => setNuevoComp({ ...nuevoComp, responsable_tipo: e.target.value })}>
            {Object.entries(RESPONSABLE_COMPROMISO).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
          <input type="date" style={{ ...s.input, flex: '1 1 140px' }} value={nuevoComp.fecha_limite} onChange={e => setNuevoComp({ ...nuevoComp, fecha_limite: e.target.value })} />
          <button type="submit" style={s.btnPrimario}>Agregar</button>
        </form>
      )}
      {compromisos.length === 0 ? <div style={es.vacio}>Sin compromisos vinculados. Su estado se gestiona en la pestaña Compromisos.</div> : (
        <div style={es.lista}>
          {compromisos.map(k => (
            <div key={k.id} style={es.item}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={es.itemTexto}>{k.descripcion}</div>
                <div style={es.meta}>{RESPONSABLE_COMPROMISO[k.responsable_tipo]}{k.fecha_limite ? ` · hasta ${fecha(k.fecha_limite)}` : ''}</div>
              </div>
              <Chip def={ESTADO_COMPROMISO[k.estado]} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Planes({ caso, onCambio }) {
  const [modo, setModo] = useState(null);            // null | 'nuevo' | id del plan en edición
  const [abierto, setAbierto] = useState(null);      // plan anterior desplegado
  const [error, setError] = useState('');
  const editable = caso.permisos.editable;
  const planes = caso.planes || [];
  const vigente = planes.find(p => PLAN_VIGENTE.includes(p.estado));
  const anteriores = planes.filter(p => p !== vigente);

  const listo = () => { setModo(null); onCambio(); };

  if (modo === 'nuevo') return <FormPlan casoId={caso.id} onListo={listo} onCancelar={() => setModo(null)} />;
  if (modo) {
    const plan = planes.find(p => p.id === modo);
    if (plan) return <FormPlan plan={plan} casoId={caso.id} onListo={listo} onCancelar={() => setModo(null)} />;
  }

  return (
    <div>
      {error && <div style={s.errorBox}>{error}</div>}

      {vigente ? (
        <Plan plan={vigente} caso={caso} editable={editable} hayVigente onCambio={onCambio} onEditar={() => setModo(vigente.id)} setError={setError} />
      ) : (
        <div style={es.sinPlan}>
          <div style={{ fontWeight: 700, color: '#1a1a2e' }}>Este caso no tiene un plan vigente.</div>
          <div style={{ fontSize: '13px', color: '#888', margin: '4px 0 12px' }}>
            El plan es opcional. Sirve para fijar un objetivo, las acciones de cada responsable y cómo medir el avance.
          </div>
          {editable && <button onClick={() => setModo('nuevo')} style={s.btnPrimario}>Crear plan de acompañamiento</button>}
        </div>
      )}

      {anteriores.length > 0 && (
        <>
          <h4 style={{ ...es.subtitulo, marginTop: '26px' }}>Planes anteriores</h4>
          <div style={es.lista}>
            {anteriores.map(p => (
              <div key={p.id} style={es.anterior}>
                <button onClick={() => setAbierto(abierto === p.id ? null : p.id)} style={es.anteriorBoton} aria-expanded={abierto === p.id}>
                  <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                    <span style={es.anteriorObjetivo}>{p.objetivo}</span>
                    <span style={es.meta}>{fecha(p.fecha_inicio || p.creado_en)}{p.fecha_objetivo ? ` → ${fecha(p.fecha_objetivo)}` : ''}</span>
                  </span>
                  <Chip def={ESTADO_PLAN[p.estado]} />
                </button>
                {abierto === p.id && (
                  <div style={{ padding: '4px 14px 14px' }}>
                    <Plan plan={p} caso={caso} editable={editable} hayVigente={!!vigente} onCambio={onCambio} onEditar={() => setModo(p.id)} setError={setError} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const es = {
  form: { maxWidth: '760px' },
  formTitulo: { fontSize: '16px', fontWeight: 800, color: '#1a1a2e', margin: '0 0 14px' },
  area: { ...s.input, minHeight: '80px', resize: 'vertical' },
  fila2: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  accionNueva: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' },
  quitar: { background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '15px', padding: '6px' },
  enlace: { background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, padding: 0, fontFamily: 'inherit' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
  periodo: { fontSize: '13px', color: '#888' },
  avance: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' },
  barra: { flex: '1 1 160px', height: '8px', background: '#eef0f5', borderRadius: '999px', overflow: 'hidden' },
  barraRelleno: { height: '100%', background: 'linear-gradient(90deg, #667eea, #43a047)', borderRadius: '999px' },
  avanceTexto: { fontSize: '12.5px', color: '#666', fontWeight: 600 },
  textoTitulo: { fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px' },
  textoValor: { fontSize: '14px', color: '#333', lineHeight: 1.55, whiteSpace: 'pre-wrap', marginTop: '2px' },
  acciones: { display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '6px 0 4px' },
  subtitulo: { fontSize: '13px', fontWeight: 800, color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px', margin: '20px 0 10px' },
  lista: { display: 'flex', flexDirection: 'column', gap: '8px' },
  item: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', background: '#fff', border: '1px solid #eef0f5', borderRadius: '12px', padding: '10px 14px' },
  vencido: { border: '1px solid #ffcdd2', background: '#fffafa' },
  itemTexto: { fontSize: '14px', color: '#333', lineHeight: 1.5 },
  meta: { fontSize: '12px', color: '#999', marginTop: '2px', display: 'block' },
  selectEstado: { ...s.input, width: 'auto', padding: '6px 10px' },
  vacio: { background: '#fafbff', border: '1px dashed #dde0f0', borderRadius: '12px', padding: '14px', textAlign: 'center', color: '#999', fontSize: '13.5px' },
  sinPlan: { background: '#fafbff', border: '1px dashed #dde0f0', borderRadius: '12px', padding: '20px', textAlign: 'center' },
  anterior: { border: '1px solid #eef0f5', borderRadius: '12px', background: '#fff' },
  anteriorBoton: { display: 'flex', gap: '10px', alignItems: 'center', width: '100%', background: 'none', border: 'none', padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit' },
  anteriorObjetivo: { display: 'block', fontSize: '14px', fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
